import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAddButtonErrorTrue } from '../Reducer/StatusFlagsReducer';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import useActionLog from './useActionLog';
import { closeLoading, showLoading } from '../Reducer/LoadingReducer';
import { useSchemaValidation } from './useSchema';
import Commons from '../Services/Common/Common';

export function useSave({
  menuId, // 메뉴(화면) 식별자
  requiredLabel = null, // 변경/삭제 항목이 없을 때 표시할 경고 메시지의 항목명
  schema = null, // 유효성 검사 스키마 객체

  getChangedRows, // 저장할 변경된 행 반환 함수
  getDeletedRows = () => [], // 삭제할 행 반환 함수
  getSelectedRows = null,

  requestDelete, // 삭제 요청 함수
  requestSave, // 저장 요청 함수

  fileDelete = null, // 행 삭제 시 파일 삭제 처리 함수
  fileSave = null, // 행 저장 시 파일 저장 처리 함수

  afterSaveRow = null, // 행 저장 후 후처리 함수
  afterDeleteRow = null, // 행 삭제 후 후처리 함수

  onSuccessRefresh = null, // 저장 성공 후 새로고침 처리 함수

  refresh = null, // 기본 새로고침 함수
  reset = null, // 기본 리셋 함수
  partialRefresh = null, // 부분 새로고침 함수
}) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [detailData, setDetailData] = useState(null);

  const { SaveActionLog } = useActionLog();
  const { validateRequiredWithMessage } = useSchemaValidation();

  // 로컬스토리지에 처리 내역 저장
  const saveToLocalHistory = (detailData, menuId) => {
    if (!detailData || detailData.length === 0) return;

    try {
      const historyKey = 'operation_history';
      const history = Commons.Storages('get', historyKey) || [];

      const successCount = detailData.filter(
        (d) => d.isSuccess === true
      ).length;
      const failCount = detailData.filter((d) => d.isSuccess === false).length;

      const newEntry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        menuId: menuId || 'Unknown',
        summary: `${detailData.length}개 항목 처리`,
        detailData: detailData,
        successCount,
        failCount,
        totalCount: detailData.length,
      };

      // 최신 항목을 앞에 추가 (최대 1000개 유지)
      const updated = [newEntry, ...history].slice(0, 1000);
      Commons.Storages('set', historyKey, updated);
    } catch (error) {
      console.error('[useSaveN] 로컬스토리지 저장 실패:', error);
    }
  };

  const run = async () => {
    dispatch(showLoading());

    let changedRows = getChangedRows();

    if (changedRows === false) {
      dispatch(closeLoading());
      return;
    }

    changedRows = changedRows || [];
    let deletedRows = getDeletedRows?.() ?? [];

    const hasDelete = deletedRows.length > 0;
    const hasChange = changedRows.length > 0;

    let saveDetailData = [];
    let deleteDetailData = [];

    const defaultSuccessRefresh = async ({ saveDetailData, changedRows }) => {
      const hasFailure = (saveDetailData ?? []).some(
        (r) => r.isSuccess === false
      );

      if (hasFailure && partialRefresh) {
        await partialRefresh({ saveResult: saveDetailData, changedRows });
        return;
      }

      if (reset) reset();
      if (refresh) await refresh();
    };

    try {
      if (getSelectedRows) {
        const selectedRows = getSelectedRows();
        if (!selectedRows || selectedRows.length === 0) {
          dispatch(
            showSnackbar({ message: '항목을 선택하세요.', type: 'warning' })
          );
          dispatch(closeLoading());
          return;
        }
      } else {
        if (!hasDelete && !hasChange && requiredLabel) {
          dispatch(setAddButtonErrorTrue());

          const message = requiredLabel
            ? `${requiredLabel}은 필수입니다.`
            : '행을 먼저 추가하거나 선택해 주세요.';

          dispatch(
            showSnackbar({
              message: message,
              type: 'warning',
            })
          );
          dispatch(closeLoading());
          return;
        }
      }

      // 저장(변경)이 있을 때: 스키마 필수검사를 삭제 API보다 먼저 — 실패 시 삭제/저장 API 모두 호출하지 않음
      if (hasChange && !validateRequiredWithMessage(changedRows, schema)) {
        return;
      }

      if (hasDelete) {
        const deleteResponse = await requestDelete?.(deletedRows);

        if (Array.isArray(deleteResponse)) {
          deleteDetailData = deleteResponse;
        } else if (deleteResponse?.data && Array.isArray(deleteResponse.data)) {
          deleteDetailData = deleteResponse.data;
        } else if (deleteResponse && typeof deleteResponse === 'object') {
          deleteDetailData = Object.values(deleteResponse).filter(
            (v) => v && typeof v === 'object' && 'isSuccess' in v
          );
        }

        if (fileDelete) {
          await fileDelete(deletedRows);
        }

        if (afterDeleteRow) {
          for (let idx = 0; idx < deletedRows.length; idx++) {
            await afterDeleteRow(deletedRows[idx], {
              index: idx,
              savedRows: deleteDetailData,
              userInfo,
            });
          }
        }
      }

      // 실제 저장 수행 (필수검증은 위에서 이미 수행)
      if (hasChange) {
        if (fileSave) {
          await fileSave(changedRows);
        }
        const saveResponse = await requestSave?.(changedRows);
        // saveResponse는 apiRequest의 반환값 (이미 data)
        // 또는 { data: [...], severity: 'success' } 형식일 수 있음
        if (saveResponse) {
          if (saveResponse.data) {
            saveDetailData = Array.isArray(saveResponse.data)
              ? saveResponse.data
              : [saveResponse.data];
          } else if (Array.isArray(saveResponse)) {
            saveDetailData = saveResponse;
          } else {
            saveDetailData = [saveResponse];
          }
        }

        if (
          saveResponse?.severity === 'success' &&
          saveDetailData.length === 0
        ) {
          console.error('저장 후 반환된 값이 없습니다.');
          return;
        }

        if (afterSaveRow) {
          for (let idx = 0; idx < changedRows.length; idx++) {
            await afterSaveRow(changedRows[idx], {
              index: idx,
              savedRows: saveDetailData,
              userInfo,
            });
          }
        }
      }

      const countResults = (dataArray) => {
        return (dataArray || []).reduce(
          (acc, item) => {
            if (item?.isSuccess === true) {
              acc.success++;
            } else {
              acc.fail++;
            }
            return acc;
          },
          { success: 0, fail: 0 }
        );
      };

      const saveCnt = countResults(saveDetailData);
      const deleteCnt = countResults(deleteDetailData);

      const totalSuccess = saveCnt.success + deleteCnt.success;
      const totalFail = saveCnt.fail + deleteCnt.fail;
      const totalCount = totalSuccess + totalFail;

      if ((hasChange || hasDelete) && totalCount === 0) {
        dispatch(
          showSnackbar({ message: '처리된 결과가 없습니다.', type: 'error' })
        );
      } else {
        const unifiedMsg = `${totalCount}개 항목 중 ${totalSuccess}개 성공, ${totalFail}개 실패로 처리되었습니다.`;
        const unifiedSeverity = totalFail > 0 ? 'error' : 'success';

        const mergedDetailData = [...deleteDetailData, ...saveDetailData];
        setDetailData(mergedDetailData.length ? mergedDetailData : null);

        // 로컬스토리지에 처리 내역 저장
        if (mergedDetailData.length > 0) {
          saveToLocalHistory(mergedDetailData, menuId);
        }

        dispatch(
          showSnackbar({
            message: unifiedMsg,
            type: unifiedSeverity,
            ...(mergedDetailData.length
              ? { detailData: mergedDetailData }
              : {}),
          })
        );
      }

      const successRefreshHandler = onSuccessRefresh ?? defaultSuccessRefresh;
      await successRefreshHandler({ saveDetailData, changedRows });

      await SaveActionLog([...changedRows, ...deletedRows], {
        menuId,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });
    } catch (e) {
      if (e?.shouldShowDialog) return;
      console.error('[useSave] ERROR CAUGHT', e);

      dispatch(
        showSnackbar({
          message: e?.message || '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );

      await SaveActionLog([...changedRows, ...deletedRows], {
        menuId,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e?.message,
      });
    } finally {
      dispatch(closeLoading());
    }
  };

  return { run, detailData };
}
